import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
// Firebase
import { projectFirestore } from "../../firebase/config";
// Hooks
import { useAuthContext } from "../../hooks/useAuthContext";
// Components
import Error from "../../components/Error";
import Loading from "../../components/Loading";
// Styles
import "../../styles/pages/Profile.css";

const Profile = () => {
  // Get profile name from URL
  const { name } = useParams();
  // Signed in user
  const { user: currentUser } = useAuthContext();
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch profile from Firestore
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Reset loading and error state
        setLoading(true);
        setError(null);

        // Fetch users collection
        const snapshot = await getDocs(collection(projectFirestore, "users"));
        // Find profile by name and keep Firestore document ID
        const matchedProfile = snapshot.docs
          .map((userDoc) => ({
            id: userDoc.id,
            ...userDoc.data(),
          }))
          .find(
            (userData) => userData.name?.toLowerCase() === decodeURIComponent(name).toLowerCase()
          );

        setProfile(matchedProfile || null);
      } catch (err) {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [name]);

  // Show loading state
  if (loading) return <Loading message="Loading profile" />;
  // Show error state
  if (error) return <Error message={error} />;
  // Show profile not found state
  if (!profile) return <Error message="Profile not found." />;

  // Check whether signed-in user owns this profile
  const isOwner =
    currentUser?.uid === profile.id ||
    currentUser?.uid === profile.uid ||
    currentUser?.email === profile.email;

  // Use Firestore document ID if legacy uid field is unavailable
  const profileId = profile.uid || profile.id;

  // Normalise GitHub URL
  const githubUrl = profile.github
    ? profile.github.startsWith("http")
      ? profile.github
      : `https://${profile.github}`
    : null;

  // Normalise LinkedIn URL
  const linkedinUrl = profile.linkedin
    ? profile.linkedin.startsWith("http")
      ? profile.linkedin
      : `https://${profile.linkedin}`
    : null;

  // Build location text without showing unnecessary commas
  const locationText = [profile.city, profile.country].filter(Boolean).join(", ");

  return (
    <main className="profile-page text-light">
      {/* PROFILE HERO */}
      <section className="profile-hero position-relative overflow-hidden">
        {/* Background Grid */}
        <div className="profile-grid position-absolute top-0 start-0 w-100 h-100" />
        <div className="container position-relative">
          {/* Back Link */}
          <Link
            to="/projects"
            className="profile-back-link d-inline-flex align-items-center gap-2 text-decoration-none mb-5"
          >
            <span>←</span>
            Back to projects
          </Link>
          <div className="row align-items-end g-5">
            {/* Profile Identity */}
            <div className="col-lg-8">
              <span className="section-label d-block mb-4">MEMBER / PROFILE</span>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-4">
                {/* Profile Picture */}
                <div className="profile-avatar-wrapper flex-shrink-0">
                  {profile.photoURL ? (
                    <img
                      src={profile.photoURL}
                      alt={profile.name}
                      className="profile-avatar rounded-circle"
                    />
                  ) : (
                    <span className="profile-avatar profile-avatar-fallback rounded-circle d-flex align-items-center justify-content-center fw-bold">
                      {profile.name?.charAt(0).toUpperCase() || "?"}
                    </span>
                  )}
                </div>
                {/* Profile Name */}
                <div>
                  <h1 className="profile-name fw-bold mb-3">{profile.name}</h1>
                  {locationText && (
                    <p className="profile-location text-secondary mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-geo-alt" />
                      {locationText}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Profile Action */}
            <div className="col-lg-4 text-lg-end">
              {isOwner && (
                <Link
                  to="/edit-profile"
                  className="btn btn-danger d-inline-flex align-items-center gap-2"
                >
                  <i className="bi bi-pencil" />
                  Edit Profile
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* PROFILE CONTENT */}
      <section className="profile-content-section">
        <div className="container">
          <div className="row g-5">
            {/* Main Content */}
            <div className="col-lg-8">
              {/* About */}
              {profile.aboutMe && (
                <section className="profile-section">
                  <span className="section-label d-block mb-3">01 / ABOUT</span>
                  <div
                    className="profile-about-content"
                    dangerouslySetInnerHTML={{
                      __html: profile.aboutMe,
                    }}
                  />
                </section>
              )}
              {/* Projects */}
              <section className={`profile-section ${profile.aboutMe ? "mt-5 pt-5" : ""}`}>
                <span className="section-label d-block mb-3">
                  {profile.aboutMe ? "02 / PROJECTS" : "01 / PROJECTS"}
                </span>
                <h2 className="profile-section-title fw-bold mb-3">
                  See what {profile.name?.split(" ")[0]} has been building.
                </h2>
                <p className="text-secondary mb-4">
                  Explore projects created by or developed with {profile.name}.
                </p>
                <Link
                  to={`/projects?user=${encodeURIComponent(profileId)}`}
                  className="btn btn-outline-light d-inline-flex align-items-center gap-2"
                >
                  View Projects
                  <span>→</span>
                </Link>
              </section>
            </div>
            {/* Profile Sidebar */}
            <div className="col-lg-4">
              <aside className="profile-sidebar">
                {/* Contact Details */}
                <div className="profile-sidebar-section">
                  <small className="profile-meta-label d-block font-monospace text-secondary mb-3">
                    CONTACT
                  </small>
                  <div className="d-flex flex-column gap-3">
                    {/* Email */}
                    {profile.email && (
                      <div>
                        <small className="text-secondary d-block mb-1">Email</small>
                        <a
                          href={`mailto:${profile.email}`}
                          className="profile-contact-link text-decoration-none"
                        >
                          {profile.email}
                        </a>
                      </div>
                    )}
                    {/* Phone */}
                    {profile.phone && (
                      <div>
                        <small className="text-secondary d-block mb-1">Phone</small>
                        <a
                          href={`tel:${profile.phone}`}
                          className="profile-contact-link text-decoration-none"
                        >
                          {profile.phone.startsWith("+") ? profile.phone : `+${profile.phone}`}
                        </a>
                      </div>
                    )}
                    {/* Location */}
                    {locationText && (
                      <div>
                        <small className="text-secondary d-block mb-1">Location</small>
                        <span>{locationText}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Connect Links */}
                {(githubUrl || linkedinUrl) && (
                  <div className="profile-sidebar-section">
                    <small className="profile-meta-label d-block font-monospace text-secondary mb-3">
                      CONNECT
                    </small>
                    <div className="d-grid gap-2">
                      {/* GitHub */}
                      {githubUrl && (
                        <a
                          href={githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-light d-flex align-items-center justify-content-between"
                        >
                          <span>
                            <i className="bi bi-github me-2" />
                            GitHub
                          </span>
                          <span>↗</span>
                        </a>
                      )}
                      {/* LinkedIn */}
                      {linkedinUrl && (
                        <a
                          href={linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-light d-flex align-items-center justify-content-between"
                        >
                          <span>
                            <i className="bi bi-linkedin me-2" />
                            LinkedIn
                          </span>
                          <span>↗</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {/* Member Status */}
                <div className="profile-sidebar-section">
                  <small className="profile-meta-label d-block font-monospace text-secondary mb-3">
                    BENOMA
                  </small>
                  <div className="d-flex align-items-center gap-2">
                    <span className="profile-status-dot rounded-circle" />
                    <span className="small">Collective member</span>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>
      {/* FOOTER CTA */}
      <section className="profile-footer border-top border-secondary-subtle">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <small className="profile-meta-label font-monospace text-secondary d-block mb-2">
                EXPLORE BENOMA
              </small>
              <h2 className="fw-bold mb-0">See what the collective has been building.</h2>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link
                to="/projects"
                className="btn btn-danger btn-lg d-inline-flex align-items-center gap-2"
              >
                All Projects
                <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Profile;
